import dayjs from 'dayjs'
import { ChtType, DashboardCategory, DashboardSettingType, DashboardSources, QueryExpression } from '../../constants/DashboardConf'
import { DataType } from '../../constants/DataType'
import { addConsumerSetting, addDashboardSetting, delConsumerSetting, delDashboardSetting, modConsumerSetting, modDashboardSetting } from '../../apis/dashboard'

const { STR, STR_WITHOUT_LIKE, DATETIME, DATE, BOOL, INT, FLOAT, STR_EQUAL_ONLY, INT_COMPLETE, STR_IN } = DataType
export const operator = ln_t => ({
  Equal: { value: 'Equal', label: ln_t('criteria.logics.equal'), component: 'input' },
  NotEqual: { value: 'NotEqual', label: ln_t('criteria.logics.notEqual'), component: 'input' },
  In: { value: 'In', label: ln_t('criteria.logics.include'), component: 'inputMultiple', multiple: true },
  NotIn: { value: 'NotIn', label: ln_t('criteria.logics.notInclude'), component: 'inputMultiple', multiple: true },
  Like: { value: 'like', label: ln_t('criteria.logics.like'), component: 'input' },
  NotLike: { value: 'NotLike', label: ln_t('criteria.logics.notLike'), component: 'input' },
  Interval: { value: 'Interval', label: ln_t('criteria.logics.interval'), component: 'rangepicker' },
  Gt: { value: 'Gt', label: '>', component: 'inputNumber', precision: 0 },
  Lt: { value: 'Lt', label: '<', component: 'inputNumber', precision: 0 },
  Gte: { value: 'Gte', label: '>=', component: 'inputNumber', precision: 0 },
  Lte: { value: 'Lte', label: '<=', component: 'inputNumber', precision: 0 },
  EqualInt: { value: 'Equal', label: '=', component: 'inputNumber', precision: 0 }
})

// ----- Operator for filters ----- //
export const operatorConf = ln_t => {
  const { Equal, NotEqual, In, NotIn, Like, NotLike, Interval, Gt, Lt, Gte, Lte, EqualInt } = operator(ln_t)
  return {
    [STR]: [Equal, NotEqual, In, NotIn, Like, NotLike],
    [STR_WITHOUT_LIKE]: [Equal, NotEqual, In, NotIn],
    [STR_EQUAL_ONLY]: [Equal],
    [STR_IN]: [In, NotIn],
    [DATETIME]: [{ ...Equal, component: 'datepicker', time: true }, { ...NotEqual, component: 'datepicker', time: true }, { ...Interval, time: true }],
    [DATE]: [{ ...Equal, component: 'datepicker' }, { ...NotEqual, component: 'datepicker' }, { ...Interval }],
    [BOOL]: [{ ...Equal, component: 'select' }],
    [INT]: [Gte, Lte, EqualInt],
    [INT_COMPLETE]: [Gte, Lte, EqualInt, Gt, Lt],
    [FLOAT]: [Gte, Lte, EqualInt]
  }
}
export const multipleOptions = ['In', 'NotIn', 'like', 'NotLike']

const { PII, PROFILE, FOOTPRINT, SUMMARY, PIECHART, BARCHART, LINECHART, MAP, SANKEY, TIMELINE, CUSTOM, TAGS } = DashboardSettingType
const { SELECT, ORDERBY, LIMIT, WHERE, GROUPBY } = QueryExpression
const { CONSUMER, GROUP, BRAND, SEGMENT } = DashboardCategory
const { DATASOURCE, THIRD_PARTY, TAXONOMY, PII_TABLE, INDICATOR, FIRSTPARTY_TAXONOMY, THIRDPARTY_TAXONOMY } = DashboardSources
const { AGE, GENDER, HOME, WORK, INTEREST, WEBSITE, APP } = ChtType
export const config = {
  default: {
    useCategoryDetail: [],
    categoryDetailOptions: [],
    useDataSource: [FOOTPRINT, SUMMARY, PIECHART, BARCHART, LINECHART, TIMELINE, CUSTOM],
    useDataSourceTree: [PIECHART, BARCHART, LINECHART, CUSTOM, TIMELINE, SANKEY],
    useLimit: [FOOTPRINT, SUMMARY, PIECHART, BARCHART, LINECHART, TIMELINE],
    chartSchema: DATASOURCE,
    hideFormActionBtn: [TIMELINE],
    useCrossData: [PIECHART, BARCHART, LINECHART, CUSTOM],
    useDateFilter: [BARCHART, LINECHART],
    apis: { addApi: addDashboardSetting, modApi: modDashboardSetting, delApi: delDashboardSetting }
  },
  [CONSUMER]: { apis: { addApi: addConsumerSetting, modApi: modConsumerSetting, delApi: delConsumerSetting }, useCrossData: [CUSTOM], useCategoryDetail: [TAGS], categoryDetailOptions: [FIRSTPARTY_TAXONOMY, THIRDPARTY_TAXONOMY] },
  [GROUP]: { useCategoryDetail: [PIECHART, BARCHART, LINECHART, SUMMARY], categoryDetailOptions: [DATASOURCE, THIRD_PARTY, TAXONOMY, INDICATOR] },
  [BRAND]: { useCategoryDetail: [PIECHART, BARCHART, LINECHART, SUMMARY], categoryDetailOptions: [DATASOURCE, THIRD_PARTY, TAXONOMY, INDICATOR] },
  [SEGMENT]: { useCategoryDetail: [PIECHART, BARCHART, LINECHART, SUMMARY], categoryDetailOptions: [DATASOURCE, THIRD_PARTY, TAXONOMY, INDICATOR], chartSchema: PII_TABLE }
}
export const chtOptions = category => {
  const includeWeb = category === 'segment' ? [WEBSITE] : []
  const chartOpt = [AGE, GENDER, INTEREST, ...includeWeb, APP]
  return {
    [PIECHART]: chartOpt,
    [BARCHART]: chartOpt,
    [LINECHART]: chartOpt,
    [MAP]: [HOME, WORK]
  }
}

export const pieAndBarFields = ['groupBy', 'xAxis', 'yAxis', 'aggregate', 'aggregateColumn', 'order', 'limit', 'filterLogic']

const anyTime = val => dayjs(val).hour() !== 0 && dayjs(val).minute() !== 0 && dayjs(val).second() !== 0
const getValDate = val => dayjs(val).format(`YYYY-MM-DD${anyTime(val) ? ' HH:mm:ss' : ''}`)
const getChartFilters = (list, keyMapping) => {
  const filterObj = {}
  Object.keys(list || {})?.forEach(i => {
    const [key, fieldName] = i?.split('-')
    const val = list[i]
    const value = Array?.isArray(val)
      ? dayjs(val[0]).isValid() && dayjs(val[1]).isValid() && val.length === 2
        ? `${getValDate(val[0])},${getValDate(val[1])}`
        : val.join(fieldName === 'column' ? '^@^' : ',')
      : dayjs(val).isValid() ? getValDate(val) : val
    const type = fieldName === 'column' ? { type: keyMapping[value]?.type } : {}
    filterObj[key] = { ...filterObj[key] || {}, [fieldName]: value, ...type }
  })
  return Object.values(filterObj)
}

const constructBarLineChart = (value, keyMapping = {}) => {
  const { aggregate, xAxis, yAxis, order, limit, filterLogic, dateFilter, ...filterList } = value
  const filters = getChartFilters(filterList, keyMapping)
  const x = Array?.isArray(xAxis) ? xAxis.join('^@^') : xAxis
  const y = Array?.isArray(yAxis) ? yAxis.join('^@^') : yAxis
  return {
    UISetting: { aggregate, xAxis, yAxis, filterLogic, filters, order, limit, dateFilter },
    query: [
      { expr: GROUPBY, columns: x, type: keyMapping[x]?.type },
      { expr: aggregate, columns: y, type: keyMapping[y]?.type },
      { expr: WHERE, logic: filterLogic, columns: filters },
      ...order ? [{ expr: ORDERBY, columns: x, value: order, type: keyMapping[x]?.type }] : [],
      ...limit ? [{ expr: LIMIT, value: limit }] : []
    ]
  }
}
export const constructToSave = {
  [PII]: value => {
    const pii = {}
    const queryCol = []
    Object.keys(value).forEach(i => {
      const [key, fieldName] = i?.split('-')
      pii[key] = { ...pii[key] || {}, [fieldName]: value[i] }
      if (fieldName === 'column') queryCol.push(value[i])
    })
    return { UISetting: { pii: Object.values(pii) }, query: [{ expr: SELECT, columns: [...new Set(queryCol)] }] }
  },
  [PROFILE]: value => {
    const contacts = {}
    const notContacts = {}
    const queryCol = []
    Object.keys(value).forEach(i => {
      const [fieldName, key] = i?.split('-')
      if (fieldName === 'contactType') { contacts[key] = { ...contacts[key] || {}, contactType: value[i] }; return }
      if (fieldName === 'contact') contacts[key] = { ...contacts[key] || {}, contact: value[i] }
      else notContacts[i] = value[i]
      queryCol.push(value[i])
    })
    return { UISetting: { ...notContacts, contacts: Object.values(contacts) }, query: [{ expr: SELECT, columns: [...new Set(queryCol)] }] }
  },
  [FOOTPRINT]: (value, keyMapping = {}) => {
    const { column, limit, order, timeline } = value
    const query = [
      { expr: SELECT, columns: [timeline, column], type: [keyMapping[timeline]?.type, keyMapping[column]?.type] },
      { expr: ORDERBY, columns: timeline, value: order, type: keyMapping[timeline]?.type },
      { expr: LIMIT, value: limit }
    ]
    return { UISetting: value, query }
  },
  [SUMMARY]: (value, keyMapping = {}) => ({ UISetting: value, query: [{ expr: value.aggregate, columns: value.column, type: keyMapping[value.column]?.type }] }),
  [PIECHART]: (value, keyMapping = {}) => {
    const { aggregate, groupBy, aggregateColumn, order, limit, filterLogic, ...filterList } = value
    const filters = getChartFilters(filterList, keyMapping)
    const group = Array?.isArray(groupBy) ? groupBy.join('^@^') : groupBy
    const aggregateCol = Array?.isArray(aggregateColumn) ? aggregateColumn.join('^@^') : aggregateColumn || group
    return {
      UISetting: { aggregate, groupBy, aggregateColumn, filterLogic, filters, order, limit },
      query: [
        { expr: GROUPBY, columns: group, type: keyMapping[group]?.type },
        { expr: aggregate, columns: aggregateCol, type: keyMapping[aggregateCol]?.type },
        { expr: WHERE, logic: filterLogic, columns: filters },
        ...order ? [{ expr: ORDERBY, columns: aggregateCol, value: order, type: keyMapping[group]?.type }] : [],
        ...limit ? [{ expr: LIMIT, value: limit, type: keyMapping[group]?.type }] : []
      ]
    }
  },
  [BARCHART]: constructBarLineChart,
  [LINECHART]: constructBarLineChart,
  [SANKEY]: (value, keyMapping = {}, dataSourceTree = {}) => {
    const { dataRange, source, ...other } = value
    const mapping = {}
    let journeyCount = []
    const filterCounter = {}
    const filterTypeIsDate = {}
    Object.entries(other)?.forEach(([i, val]) => {
      if (!val) return

      const [journeyNum, key, filterField] = i?.split('-')
      const journey = `journey${journeyNum}`
      if (!mapping[journey]) {
        mapping[journey] = { table: {}, filters: [] }
        journeyCount = [...journeyCount, Number(journeyNum)]
      }

      if (key === 'dataSourceId') mapping[journey].table.type = dataSourceTree?.find(i => i.dataSourceId === val)?.dataSourceType
      if (key === 'dataSourceId' || key === 'tableName') { mapping[journey].table = { ...mapping[journey].table, [key]: val }; return }
      if (!filterField) { mapping[journey] = { ...mapping[journey], [key]: val }; return }
      if (filterField !== 'column') return

      const uniqueKey = `${other[`${journeyNum}-dataSourceId`]}^@^${other[`${journeyNum}-tableName`]}^@^${val}`
      const newFilter = { column: val, type: keyMapping[uniqueKey]?.type, operator: other[`${journeyNum}-${key}-operator`], value: other[`${journeyNum}-${key}-value`] }
      filterTypeIsDate[`${journeyNum}-${key}`] = newFilter.type === DATE || newFilter.type === DATETIME || newFilter.column === 'm' || newFilter.column === 'd'
      const value = newFilter.type === DATE || newFilter.type === DATETIME || newFilter.column === 'm' || newFilter.column === 'd'
        ? Array.isArray(newFilter.value)
          ? newFilter.value.map(i => getValDate(i)).join(',')
          : getValDate(newFilter.value)
        : Array.isArray(newFilter.value) ? newFilter.value.join(',') : newFilter.value
      mapping[journey].filters = [...mapping[journey].filters, { ...newFilter, value }]
      filterCounter[journeyNum] = [...filterCounter[journeyNum] || [], Number(key)]
    })
    return {
      UISetting: { journeys: { dataRange, ...other }, journeyCount, filterCounter, filterTypeIsDate },
      query: [{ period: dataRange, ...mapping }],
      error: journeyCount < 2 ? 'message.error.sankeyMinJourney' : false
    }
  },
  [TIMELINE]: (value, keyMapping = {}) => {
    const { limit, order, ...other } = value
    const mapping = {}
    let sourceCount = []
    const filterCounter = {}
    const filterTypeIsDate = {}
    const crossDataAll = {}
    const crossDataCount = {}
    Object.entries(other)?.forEach(([i, val]) => {
      if (!val) return

      const [source, key, filterField, crossDataField] = i?.split('-')
      if (!mapping[source]) {
        mapping[source] = { dataSourceId: '', tableName: '', rules: [], filters: [] }
        sourceCount = [...sourceCount, Number(source)]
      }

      if (key === 'dataSourceId' || key === 'tableName') { mapping[source] = { ...mapping[source], [key]: val }; return }
      if (!filterField) { mapping[source] = { ...mapping[source], [key]: Array.isArray(val) ? val.join('^@^') : val }; return }
      if (crossDataField) crossDataAll[source] = { ...crossDataAll[source] || {}, [`${key}-${filterField}-${crossDataField}`]: val }
      if (filterField !== 'column') return

      const uniqueKey = Array.isArray(val) ? val.join('^@^') : `${other[`${source}-dataSourceId`]}^@^${other[`${source}-tableName`]}^@^${val}`
      const newFilter = { column: Array.isArray(val) ? val.join('^@^') : val, type: keyMapping[uniqueKey]?.type, operator: other[`${source}-${key}-operator`], value: other[`${source}-${key}-value`] }
      filterTypeIsDate[`${source}-${key}`] = newFilter.type === DATE || newFilter.type === DATETIME || newFilter.column === 'm' || newFilter.column === 'd'
      const value = newFilter.type === DATE || newFilter.type === DATETIME || newFilter.column === 'm' || newFilter.column === 'd'
        ? Array.isArray(newFilter.value)
          ? newFilter.value.map(i => getValDate(i)).join(',')
          : getValDate(newFilter.value)
        : Array.isArray(newFilter.value) ? newFilter.value.join(',') : newFilter.value
      mapping[source].filters = [...mapping[source].filters, { ...newFilter, value }]
      filterCounter[source] = [...filterCounter[source] || [], Number(key)]
    })

    // ----- processing cross data ----- //
    if (Object.keys(crossDataAll).length) {
      Object.entries(crossDataAll)?.forEach(([key, val]) => {
        crossDataCount[key] = [...new Set(Object.keys(crossDataAll[key])?.map(i => Number(i.split('-')?.[0])))]
        crossDataAll[key] = crossDataFormatSave(val, crossDataCount[key])
      })
    }

    Object.entries(mapping)?.forEach(([key, val]) => {
      const { predefineOpt, filters, filterLogic, time, timeFormat, content, ...other } = val || {}
      mapping[key] = {
        ...other,
        rules: [
          { expr: 'DEFAULT', value: predefineOpt },
          ...filterLogic && filters?.length ? [{ expr: WHERE, logic: filterLogic, columns: filters }] : [],
          ...time && timeFormat && content ? [{ expr: SELECT, value: [time, content] }, { expr: 'TIME_FORMAT', value: timeFormat }] : []
        ],
        ...crossDataAll[key] ? crossDataAll[key] : {}
      }
    })
    return {
      UISetting: { timeline: { ...other, order, limit }, sourceCount, filterCounter, filterTypeIsDate, crossDataCount },
      query: [
        ...limit ? [{ expr: LIMIT, value: limit }] : [],
        { expr: ORDERBY, value: order },
        { expr: 'TIMELINE', columns: Object.values(mapping) }
      ]
    }
  },
  [INDICATOR]: (value) => {
    const { aggregate, dimensionId, indicatorId: indic, period, timeRange, xAxis = 'dimension', customRange = [], indicatorDateGranularity, limit, order, dateFilter, ...other } = value || {}
    const filterMapping = {}
    Object.entries(other)?.forEach(([objKey, val]) => {
      const [key, filterField] = objKey?.split('-')
      if (!filterField) return
      filterMapping[key] = { ...filterMapping[key] || {}, [filterField]: filterField === 'value' && Array.isArray(val) ? val?.join(',') : val }
    })
    const filters = Object.values(filterMapping)
    const isMonthly = indicatorDateGranularity === 'monthly' ? 'yes' : 'no'
    const startDate = isMonthly === 'yes' ? dayjs(customRange[0]).startOf('month').format('YYYY-MM-DD') : dayjs(customRange[0]).format('YYYY-MM-DD')
    const endDate = isMonthly === 'yes' ? dayjs(customRange[1]).endOf('month').format('YYYY-MM-DD') : dayjs(customRange[1]).format('YYYY-MM-DD')
    const customize = timeRange === 'custom' ? { period, aggregate, startDate, endDate } : null
    const defaultPeriod = timeRange === 'custom' ? null : timeRange
    const indicatorId = Array.isArray(indic) ? indic : [indic]
    return {
      UISetting: { aggregate, dimensionId, indicatorId, period, timeRange, xAxis, customRange, indicatorDateGranularity, filters, limit, order, dateFilter },
      query: [{ dimensionId, indicatorId, xAxis, isMonthly, customize, filters, defaultPeriod, limit, order }]
    }
  },
  [CUSTOM]: (value, keyMapping) => {
    const columns = {}
    const queryCol = []
    Object.keys(value).forEach(i => {
      const [key, fieldName] = i?.split('-')
      columns[key] = { ...columns[key] || {}, [fieldName]: value[i] }
      if (fieldName === 'column') queryCol.push(Array.isArray(value[i]) ? value[i]?.join('^@^') : value[i])
    })
    return { UISetting: { pii: Object.values(columns) }, query: [{ expr: SELECT, columns: [...new Set(queryCol)] }] }
  },
  [TAGS]: value => ({ UISetting: { pii: [{ column: TAGS, sort: 1 }], type: TAGS }, query: [{ columns: [TAGS] }] })
}

const convertCounterFields = (list, initialValue = {}) => {
  const customVal = { ...initialValue }
  const counterVal = list.map((i, idx) => {
    Object.keys(i)?.forEach(name => {
      const value = name === 'column' && `${i[name]}`.includes('^@^')
        ? i[name].split('^@^')
        : name === 'value' && `${i[name]}`.includes(',') && !Array.isArray(i[name])
          ? i[name].split(',')
          : name === 'value' && (i.type === DATE || i.type === DATETIME || i.column === 'd' || i.column === 'm')
            ? dayjs(i[name])
            : i[name]
      customVal[`${idx + 1}-${name}`] = value
    })
    return idx + 1
  })
  return { customVal, counterVal, needSetCounter: true }
}
export const constructFormEditMode = {
  [PII]: ({ pii }) => convertCounterFields(pii),
  [PROFILE]: value => {
    const { contacts, ...other } = value
    return convertCounterFields(contacts, other)
  },
  [FOOTPRINT]: value => ({ customVal: value }),
  [SUMMARY]: value => ({ customVal: value }),
  [PIECHART]: value => {
    const { filters, ...other } = value
    return convertCounterFields(filters, other)
  },
  [BARCHART]: value => {
    const { filters, ...other } = value
    return convertCounterFields(filters, other)
  },
  [LINECHART]: value => {
    const { filters, ...other } = value
    return convertCounterFields(filters, other)
  },
  [SANKEY]: value => {
    const needDateFormat = Object.entries(value?.filterTypeIsDate)?.filter(([key, val]) => val)?.map(([key]) => key) || []
    const formatted = needDateFormat.reduce((all, i) => {
      const val = value.journeys[`${i}-value`]
      return ({ ...all, [`${i}-value`]: Array.isArray(val) ? val.map(x => dayjs(x)) : dayjs(val) })
    }, [])
    return ({ customVal: { ...value.journeys, ...formatted } })
  },
  [TIMELINE]: value => {
    const needDateFormat = Object.entries(value?.filterTypeIsDate)?.filter(([key, val]) => val)?.map(([key]) => key) || []
    const formatted = needDateFormat.reduce((all, i) => {
      const val = value.timeline[`${i}-value`]
      return ({ ...all, [`${i}-value`]: Array.isArray(val) ? val.map(x => dayjs(x)) : dayjs(val) })
    }, [])
    return ({ customVal: { ...value.timeline, ...formatted } })
  },
  [INDICATOR]: value => {
    const { filters = [], customRange, ...etc } = value
    const other = { ...etc, customRange: customRange?.map(i => dayjs(i)) }
    return convertCounterFields(filters, other)
  },
  [CUSTOM]: ({ pii }) => convertCounterFields(pii)
}

export const crossDataFormatSave = (value, count) => {
  const getFormVal = key => ({ dataSourceId: value[`${key}-dataSource`], tableName: value[`${key}-tableName`], joinKey: value[`${key}-joinKey`] })
  const data = count?.map(i => ({
    sequence: i - 1,
    ...value[`${i}-logic`] ? { logic: value[`${i}-logic`] } : {},
    parent: getFormVal(`${i}-parent`),
    child: getFormVal(`${i}-child`)
  }))
  return { joinDataSource: data }
}

export const constructCrossDataForm = value => {
  const crossDataFields = ['dataSource', 'tableName', 'joinKey']
  const formObj = {}
  value.forEach(item => {
    ['parent', 'child'].forEach(type => crossDataFields?.forEach(field => {
      const key = `${item.sequence + 1}-${type}-${field}`; formObj[key] = item[type][field === 'dataSource' ? 'dataSourceId' : field]
    }))
    if (item?.logic) formObj[`${item.sequence + 1}-logic`] = item?.logic
  })
  return formObj
}
